import { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Link } from '@mui/material';
import InfoWithPopOver from './InfoWithPopOver';
import {
  getUnitGropAndUnitsList,
  getUserName,
  isScopeAnyGlobalType,
  openLink,
  utf8SafeDecode
} from '../shared/UtilFunctions';
import { callUserInfoAPI } from '../shared/APIHelper';

export default function StrategyTableView({ strategyList, commonProps }) {
  const { setStatusErrorMsg, incrementAPIInProgress, decrementAPIInProgress } =
    commonProps;
  const [userInfoMap, setUserInfoMap] = useState(null);

  const uniqueStrategies = useMemo(() => {
    if (!Array.isArray(strategyList) || strategyList.length === 0) {
      return [];
    }

    const map = new Map();
    strategyList.forEach((row) => {
      if (row?.strategyId && !map.has(row.strategyId)) {
        map.set(row.strategyId, row);
      }
    });

    return [...map.values()];
  }, [strategyList]);

  const ownerIds = useMemo(
    () =>
      uniqueStrategies
        .map((row) => row?.ownerId)
        .filter(Boolean)
        .sort(),
    [uniqueStrategies]
  );

  const columns = useMemo(
    () => [
      {
        field: 'mgcs',
        headerName: 'MGC',
        flex: 1,
        minWidth: 0,
        renderCell: ({ row }) => (
          <Box sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
            <Link
              onClick={(e) => openLink('/viewStrategy/' + row.strategyId, e)}
            >
              <InfoWithPopOver
                infoList={[...(row.decorated?.mgcs || [])].sort()}
              />
            </Link>
          </Box>
        )
      },
      {
        field: 'scope',
        headerName: 'Scope',
        flex: 1,
        minWidth: 0,
        renderCell: ({ row }) =>
          isScopeAnyGlobalType(row.scope.level) ? (
            row.scope.level
          ) : (
            <Box sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
              <InfoWithPopOver
                infoList={[
                  ...getUnitGropAndUnitsList(row.decorated.scope)
                ].sort()}
              />
            </Box>
          )
      },
      {
        field: 'ownerName',
        headerName: 'Owner',
        flex: 1,
        minWidth: 0,
        valueGetter: (_value, row) => row.ownerName ?? row.ownerId
      }
    ],
    []
  );

  useEffect(() => {
    if (ownerIds.length === 0) {
      setUserInfoMap(null);
      return;
    }

    callUserInfoAPI(
      ownerIds,
      setUserInfoMap,
      null,
      incrementAPIInProgress,
      decrementAPIInProgress,
      setStatusErrorMsg,
      true
    );
  }, [ownerIds.join('|')]);

  const rowsWithOwnerName = useMemo(() => {
    if (uniqueStrategies.length === 0) {
      return [];
    }

    return uniqueStrategies.map((item) => {
      const ownerName = utf8SafeDecode(
        userInfoMap ? getUserName(item.ownerId, userInfoMap) : item.ownerId
      );
      return {
        ...item,
        ownerName
      };
    });
  }, [uniqueStrategies, userInfoMap]);

  if (rowsWithOwnerName.length === 0) {
    return null;
  }

  const rowCount = rowsWithOwnerName.length;
  const headerHeight = 56;
  const rowHeight = 40;
  const maxBodyRows = 6;
  const visibleRows = Math.min(rowCount, maxBodyRows);
  const gridHeight = headerHeight + visibleRows * rowHeight + 2;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: gridHeight,
        maxHeight: 320,
        overflowX: 'hidden',
        overflowY: 'hidden'
      }}
    >
      <DataGridPro
        rows={rowsWithOwnerName}
        columns={columns}
        getRowId={(row) => row.strategyId}
        autoHeight={false}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={false}
        rowHeight={rowHeight}
        columnHeaderHeight={headerHeight}
        disableRowSelectionOnClick
        density="compact"
        hideFooter={rowsWithOwnerName.length <= 100}
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          height: '100%',
          maxHeight: '100%',
          '& .MuiDataGrid-main': {
            minWidth: 0,
            maxWidth: '100%'
          },
          '& .MuiDataGrid-cell': {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
            borderRight: 'none'
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto',
            overflowX: 'hidden'
          }
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'ownerName', sort: 'asc' }]
          }
        }}
      />
    </Box>
  );
}
